<template>
  <b-modal
    :active.sync="isModalActive"
    :width="720"
    scroll="keep"
    class="weekly-associate-engineering-measurement-modal"
    @close="onModalClose"
  >
    <div class="weekly-associate-engineering-modal">
      <div class="weekly-associate-engineering-modal__header">
        <div class="weekly-associate-engineering-modal__title-row">
          <b-icon
            pack="fas"
            icon="ruler-combined"
            size="is-small"
            class="weekly-associate-engineering-modal__title-icon"
          />
          <div>
            <h2 class="weekly-associate-engineering-modal__title">
              {{ $t('weekly_associate_engineering_measurement_title') }}
            </h2>
            <p class="weekly-associate-engineering-modal__subtitle">
              {{ $t('weekly_associate_engineering_measurement_subtitle') }}
            </p>
          </div>
        </div>
      </div>

      <div class="weekly-associate-engineering-modal__body">
        <div v-if="activityName" class="weekly-associate-engineering-modal__activity-box">
          {{ $t('weekly_associate_engineering_for_activity') }}
          <strong>{{ activityName }}</strong>
        </div>

        <b-field :label="$t('measurement_name')">
          <b-autocomplete
            v-model="measurementSearch"
            :data="filteredManualMeasurements"
            :placeholder="$t('select_manual_measurement')"
            field="name"
            open-on-focus
            :loading="isLoadingMeasurements"
            :disabled="isLoadingMeasurements"
            class="weekly-associate-engineering-modal__measurement-autocomplete"
            @select="onMeasurementSelected"
            @typing="onMeasurementTyping"
          >
            <template v-slot="props">
              <div class="text-xs">
                <span v-if="props.option.code" class="font-semibold">{{ props.option.code }}</span>
                <span v-if="props.option.code"> · </span>
                <span>{{ props.option.name }}</span>
              </div>
            </template>
            <template #empty>{{ $t('no_results') }}</template>
          </b-autocomplete>
        </b-field>

        <b-field :label="$t('economic_chapter')">
          <b-input
            :value="selectedChapterLabel"
            :placeholder="$t('weekly_no_economic_chapter')"
            readonly
          />
        </b-field>

        <div class="weekly-associate-engineering-modal__metrics">
          <b-field :label="$t('unit')">
            <b-input
              :value="selectedMeasurement ? selectedMeasurement.unit : ''"
              :placeholder="$t('measurement_unit_placeholder')"
              readonly
            />
          </b-field>

          <b-field :label="$t('quantity')">
            <b-numberinput
              v-model="quantity"
              :min="0"
              :max="quantityMax"
              controls-position="compact"
              size="is-small"
              icon-pack="fas"
            />
          </b-field>

          <b-field :label="$t('unit_cost')">
            <b-input
              :value="formattedUnitPrice"
              :placeholder="$t('unit_cost')"
              readonly
            />
          </b-field>
        </div>
      </div>

      <div class="weekly-associate-engineering-modal__footer">
        <button
          type="button"
          class="weekly-associate-engineering-modal__cancel-btn"
          @click="close"
        >
          {{ $t('cancel') }}
        </button>
        <button
          type="button"
          class="weekly-associate-engineering-modal__submit-btn"
          :disabled="isSaving || !canSubmit"
          @click="submit"
        >
          {{ $t('weekly_associate_measurement') }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

function buildBoqRootsGroupedRequest(projectId, activityId) {
  return {
    url: `/api/v2/project/${projectId}/activity/${activityId}/boq-roots/grouped`,
  }
}

function resolveBoqRootUnit(boqRoot) {
  if (!boqRoot || !boqRoot.measure_unit) {
    return ''
  }
  return boqRoot.measure_unit.code || boqRoot.measure_unit.name || ''
}

function resolveBoqRootParentCode(boqRoot) {
  if (!boqRoot || !boqRoot.parent) {
    return null
  }
  if (boqRoot.parent.code) {
    return boqRoot.parent.code
  }
  if (boqRoot.parent.chapter && boqRoot.parent.chapter.code) {
    return boqRoot.parent.chapter.code
  }
  return null
}

function resolveBoqRootChapterLabel(boqRoot) {
  if (!boqRoot || !boqRoot.parent) {
    return null
  }
  if (boqRoot.parent.name) {
    return boqRoot.parent.name
  }
  if (boqRoot.parent.chapter && boqRoot.parent.chapter.name) {
    return boqRoot.parent.chapter.name
  }
  const code = resolveBoqRootParentCode(boqRoot)
  return code || null
}

function mapManualBoqRootOption(boqRoot) {
  const available = boqRoot.available_units != null ? parseFloat(boqRoot.available_units) : null
  const unitPrice = boqRoot.unit_price != null && boqRoot.unit_price !== ''
    ? parseFloat(boqRoot.unit_price)
    : 0

  return {
    boq_root_id: boqRoot.id,
    code: boqRoot.code || '',
    name: boqRoot.name || '',
    unit: resolveBoqRootUnit(boqRoot),
    measure_unit_id: boqRoot.measure_unit_id
      || (boqRoot.measure_unit && boqRoot.measure_unit.id)
      || null,
    chapter_code: resolveBoqRootParentCode(boqRoot),
    chapter_label: resolveBoqRootChapterLabel(boqRoot),
    unit_price: unitPrice,
    unit_cost: unitPrice,
    available: available != null && !Number.isNaN(available) ? available : null,
    searchText: `${boqRoot.code || ''} ${boqRoot.name || ''}`.trim().toLowerCase(),
  }
}

export default {
  name: 'weekly-associate-engineering-measurement-modal',

  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      isModalActive: false,
      isSaving: false,
      isLoadingMeasurements: false,
      activityId: null,
      activityName: null,
      manualMeasurementRows: [],
      measurementSearch: '',
      measurementQuery: '',
      selectedMeasurement: null,
      quantity: 0,
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    effectiveProjectId() {
      return this.projectId || (this.project && this.project.id) || null
    },

    filteredManualMeasurements() {
      const query = String(this.measurementQuery || '').trim().toLowerCase()
      if (!query) {
        return this.manualMeasurementRows
      }
      return this.manualMeasurementRows.filter((item) => item.searchText.includes(query))
    },

    selectedChapterLabel() {
      if (!this.selectedMeasurement) {
        return ''
      }
      if (this.selectedMeasurement.chapter_label) {
        return this.selectedMeasurement.chapter_label
      }
      if (this.selectedMeasurement.chapter_code) {
        return this.selectedMeasurement.chapter_code
      }
      return ''
    },

    formattedUnitPrice() {
      if (!this.selectedMeasurement || this.selectedMeasurement.unit_price == null) {
        return ''
      }
      const value = parseFloat(this.selectedMeasurement.unit_price)
      if (Number.isNaN(value)) {
        return ''
      }
      return `${value.toFixed(2)} €`
    },

    quantityMax() {
      if (!this.selectedMeasurement || this.selectedMeasurement.available == null) {
        return undefined
      }
      const max = Math.floor(parseFloat(this.selectedMeasurement.available))
      return Number.isNaN(max) ? undefined : max
    },

    canSubmit() {
      if (!this.selectedMeasurement || !this.activityId) {
        return false
      }
      if (!this.selectedMeasurement.measure_unit_id) {
        return false
      }
      const qty = parseFloat(this.quantity)
      if (Number.isNaN(qty) || qty <= 0) {
        return false
      }
      if (this.quantityMax != null && qty > this.quantityMax) {
        return false
      }
      return true
    },
  },

  methods: {
    show({ activity = null } = {}) {
      this.activityId = activity && activity.id != null ? activity.id : null
      this.activityName = activity && activity.name ? activity.name : null
      this.resetForm()
      this.isModalActive = true
      this.fetchManualMeasurements()
    },

    resetForm() {
      this.measurementSearch = ''
      this.measurementQuery = ''
      this.selectedMeasurement = null
      this.quantity = 0
      this.manualMeasurementRows = []
    },

    async fetchManualMeasurements() {
      if (!this.effectiveProjectId || !this.activityId) {
        this.manualMeasurementRows = []
        return
      }

      const { url } = buildBoqRootsGroupedRequest(this.effectiveProjectId, this.activityId)
      this.isLoadingMeasurements = true

      try {
        const { data } = await axios.get(url)
        if (!data || !data.success) {
          this.manualMeasurementRows = []
          return
        }

        const manual = Array.isArray(data.manual) ? data.manual : []
        this.manualMeasurementRows = manual.map((boqRoot) => mapManualBoqRootOption(boqRoot))
      } catch (error) {
        this.manualMeasurementRows = []
        console.error('[weekly-associate-engineering-measurement-modal] load manual measurements error:', error)
      } finally {
        this.isLoadingMeasurements = false
      }
    },

    onMeasurementTyping(value) {
      this.measurementQuery = value != null ? String(value) : ''
      if (!String(value || '').trim()) {
        this.selectedMeasurement = null
        this.quantity = 0
      }
    },

    onMeasurementSelected(option) {
      if (!option) {
        this.selectedMeasurement = null
        this.quantity = 0
        return
      }

      this.selectedMeasurement = option
      this.measurementSearch = option.name || ''
      this.measurementQuery = option.name || ''
      this.quantity = 0
    },

    async submit() {
      if (this.isSaving || !this.canSubmit) {
        return
      }

      const selected = this.selectedMeasurement
      const payload = {
        assignmentType: 'resource',
        code: selected.code,
        name: selected.name,
        measure_unit_id: selected.measure_unit_id,
        boq_root_id: selected.boq_root_id,
        total_units: parseFloat(this.quantity),
        unit_price: selected.unit_price != null ? selected.unit_price : 0,
        unit_cost: selected.unit_cost != null ? selected.unit_cost : 0,
        description: '',
      }

      this.isSaving = true
      try {
        const { data } = await axios.post(
          `/api/v2/assignments/activity/${this.activityId}`,
          payload
        )

        if (data && data.success) {
          this.$notify.success('assignment_insert_success')
          this.$emit('created', {
            activity_id: this.activityId,
            boq_root_id: selected.boq_root_id,
            assignment_id: data.assignment && data.assignment.id ? data.assignment.id : null,
          })
          this.close()
          return
        }

        const msg = data && data.message ? data.message : 'assignment_insert_error'
        this.$notify.error(this.$t(msg))
      } catch (error) {
        let msg = 'assignment_insert_error'
        if (error && error.response && error.response.status === 422 && error.response.data.errors) {
          const errors = error.response.data.errors
          const firstKey = Object.keys(errors)[0]
          if (firstKey && errors[firstKey] && errors[firstKey][0]) {
            msg = errors[firstKey][0]
          }
        } else if (error && error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message
        }
        this.$notify.error(typeof msg === 'string' && this.$t(msg) !== msg ? this.$t(msg) : msg)
      } finally {
        this.isSaving = false
      }
    },

    close() {
      this.isModalActive = false
    },

    onModalClose() {
      this.activityId = null
      this.activityName = null
      this.isSaving = false
      this.resetForm()
    },
  },
}
</script>

<style scoped>
.weekly-associate-engineering-modal {
  background-color: #fff;
  border-radius: 0.25rem;
  overflow: visible;
}

.weekly-associate-engineering-modal__header {
  padding: 0.875rem 1.25rem;
  background: linear-gradient(90deg, #2c426c 0%, #3b66bc 100%);
  color: #fff;
}

.weekly-associate-engineering-modal__title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}

.weekly-associate-engineering-modal__title-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.weekly-associate-engineering-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.weekly-associate-engineering-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  opacity: 0.9;
}

.weekly-associate-engineering-modal__body {
  padding: 1rem 1.25rem;
  overflow: visible;
}

.weekly-associate-engineering-modal__measurement-autocomplete {
  width: 100%;
}

.weekly-associate-engineering-modal__measurement-autocomplete >>> .dropdown-menu {
  z-index: 50;
}

.weekly-associate-engineering-modal__activity-box {
  margin-bottom: 1rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid #c5d4eb;
  border-radius: 0.25rem;
  background-color: #eef3fb;
  font-size: 0.8125rem;
  color: #1f2937;
}

.weekly-associate-engineering-modal__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.weekly-associate-engineering-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem 1.25rem;
}

.weekly-associate-engineering-modal__cancel-btn,
.weekly-associate-engineering-modal__submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 7rem;
  padding: 0.375rem 0.875rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-associate-engineering-modal__cancel-btn {
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #374151;
}

.weekly-associate-engineering-modal__cancel-btn:hover {
  background-color: #f9fafb;
}

.weekly-associate-engineering-modal__submit-btn {
  border: 1px solid #3b66bc;
  background-color: #3b66bc;
  color: #fff;
}

.weekly-associate-engineering-modal__submit-btn:hover:not(:disabled) {
  background-color: #2c426c;
  border-color: #2c426c;
}

.weekly-associate-engineering-modal__submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>

<style>
/* Evitar que el contenedor del modal recorte el dropdown del autocomplete */
.weekly-associate-engineering-measurement-modal .modal-content,
.weekly-associate-engineering-measurement-modal .animation-content {
  overflow: visible;
}
</style>
